import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { PlanSteps, type PlanStep } from './PlanSteps';
import { IvyTurn, WorkingLine, YourMessage } from './Thread';

export type { PlanStep, PlanStepState } from './PlanSteps';

export interface PlanScene {
  title: string;
  state: 'planned' | 'building' | 'built';
}

export interface PlanRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface PlanTakeoverProps {
  /** The person's words, shown as their message. */
  prompt: string;
  /** A working line under the message; null hides it. */
  status?: string | null;
  /** The plan's lead line; null hides the plan. */
  reply?: string | null;
  /** False once nothing is running: the current step and the placeholder cards stop moving. */
  working?: boolean;
  /** A short line under the reply that stays while Ivy's lines change. */
  note?: string | null;
  steps?: PlanStep[];
  /** The storyboard: a count of placeholder cards, or the scenes themselves. */
  scenes?: number | PlanScene[];
  /** Where the message slides in from, in viewport pixels: the box it was typed in. */
  from?: PlanRect | null;
  /** Pinned under the conversation. */
  footer?: ReactNode;
  /** Already on screen (a new page picking up the same view): no entry motion. */
  still?: boolean;
}

const FLY_MS = 480;

function useSlideIn(from: PlanRect | null | undefined) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !from || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const to = el.getBoundingClientRect();
    const dx = from.left + from.width / 2 - (to.left + to.width / 2);
    const dy = from.top + from.height / 2 - (to.top + to.height / 2);
    el.animate(
      [{ transform: `translate(${dx}px, ${dy}px)`, opacity: 0.7 }, { transform: 'none', opacity: 1 }],
      { duration: FLY_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, from where it was typed
  return ref;
}

function Storyboard({ scenes, working }: { scenes: number | PlanScene[]; working: boolean }) {
  const cards: (PlanScene | null)[] = typeof scenes === 'number' ? Array.from({ length: scenes }, () => null) : scenes;
  if (!cards.length) return null;
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" aria-label="Storyboard">
      {cards.map((scene, i) => (
        <li
          key={i}
          data-state={scene?.state ?? 'planned'}
          className="plan-rise flex flex-col gap-1.5"
          style={{ animationDelay: `${300 + i * 60}ms` }}
        >
          <span
            className={cn(
              'block aspect-video rounded-[var(--b-radius-card)] bg-b-raised',
              working && scene?.state !== 'built' && 'plan-skeleton',
            )}
          />
          <span className="truncate text-b-meta text-b-t3">{scene ? scene.title : `Scene ${i + 1}`}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The first view of a video: the person's words, what is happening now, and the plan with its storyboard. Props
 * only; the page decides what is shown when (a staged timeline before sign-in, live events in the studio).
 */
export function PlanTakeover({ prompt, status = null, working = true, reply = null, note = null, steps = [], scenes = 0, from = null, footer, still = false }: PlanTakeoverProps) {
  const message = useSlideIn(still ? null : from);
  return (
    <div className={cn('mx-auto flex min-h-full w-full max-w-[720px] flex-col px-4', still && 'plan-still')}>
      <div className="flex-1 space-y-5 pb-6 pt-8 sm:pt-12">
        <YourMessage ref={message}>{prompt}</YourMessage>
        <IvyTurn mark={{ state: status ? 'working' : reply ? 'done' : 'idle' }}>
          {reply && (
            <>
              <p className="plan-rise text-b-read text-b-t1">{reply}</p>
              {note && <p className="text-b-meta text-b-t2">{note}</p>}
              {steps.length > 0 && <PlanSteps steps={steps} working={working} />}
              <Storyboard scenes={scenes} working={working} />
            </>
          )}
          {status && <WorkingLine text={status} />}
        </IvyTurn>
      </div>
      {footer && <div className="sticky bottom-0 bg-b-ground pb-4 pt-2">{footer}</div>}
    </div>
  );
}
